import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Fab,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RefreshIcon from "@mui/icons-material/Refresh";
import { colors } from "brand";
import { PinLock, TableSkeleton, logoutCognito } from "common-fe";
import { useTranslation } from "i18n-fe";
import { RIDE_STATUSES } from "score-common";
import { useAnnouncerContext } from "../context/AnnouncerContext";
import { ringsFor } from "../utils/rings";
import { useClassesForShow } from "../hooks/useClassesForShow";
import { useRidesForRing } from "../hooks/useRidesForRing";
import { useBreaksForRing } from "../hooks/useBreaksForRing";
import { usePeople, useHorses, useTestsForRides } from "../hooks/useRoster";
import { useRideStatuses } from "../hooks/useRideStatuses";
import { useLastPrefetchedAt } from "../hooks/useLastPrefetchedAt";
import {
  buildOrderOfGoRows,
  findNextEvent,
  findRoleRideId,
  groupRowsByDay,
  ROW_KIND,
  RIDE_ROLE,
} from "../utils/orderOfGo";
import { formatTime, formatVenueClock, venueDayKey, venueTodayKey, dayKeyLabel } from "../utils/formatTime";
import { formatClockTime } from "../utils/formatClock";
import RideRow from "./RideRow";
import ClassResults from "./ClassResults";

// #642 — THE app: one live, time-ordered running order for ONE ring.
//
// Read-only by construction. The announcer never writes a score, never changes a
// status, never touches FVD — which is what keeps this screen small.
//
// Freshness, per docs/score/announcer.md:
//   - ride STATUSES change constantly during a class -> poll every 5s
//     (useRideStatuses; it already pauses on a hidden tab and refreshes on
//     return, so nothing here needs a visibility listener of its own).
//   - rides / breaks / classes change on a scratch, a schedule reorder, or a
//     re-ingest -> cache-first from IndexedDB, revalidate on the SAME 5s beat
//     (#692 — a class reorder/scratch/moved break must show up on the very
//     next poll, and Evin needs to see an FVD change within 5s). people/horses
//     stay on their own one-shot fetch (useRoster.js) — org-wide roster data
//     that isn't part of the running order.
//
// #692 polling cost, one ring/terminal, one show day (all single-query GSI
// reads, no per-class fan-out — #637's showId/ring filter on GET /ride):
//   GET /ride?showId&ring      (ridesByShow GSI)    -> 720/hr
//   GET /break?showId&ring     (breaksByShow GSI)   -> 720/hr
//   GET /class?showId                               -> 720/hr
//   POST /rides/status (batch)                       -> 720/hr
//   ~2,880 reads/hr/terminal. Cheap: each is one indexed query, not a scan,
//   and a full 8-hour show day is well under 25k reads per terminal even with
//   several terminals running at once.
const STATUS_POLL_MS = 5000;
const ENTITY_POLL_MS = 5000;
// #645 — how long the screen can go without a successful status refresh
// before it stops calling itself "live", even while `online` is still true.
// A single missed poll (a blip) shouldn't flash the banner; four missed polls
// (STATUS_POLL_MS * 4) means something's actually wrong upstream.
const STALE_THRESHOLD_MS = STATUS_POLL_MS * 4;
// How often the banner's own clock re-checks staleness / re-renders its
// "HH:MM" text. Independent of the entity/status polls — this never touches
// the network, it just re-evaluates wall-clock math.
const CLOCK_TICK_MS = 10000;
// #691 — the sticky header's live clock + countdown want a proper ticking
// feel, so they get their own faster tick, separate from the freshness
// banner's coarser one above. Still fetches nothing — pure wall-clock math.
const LIVE_TICK_MS = 1000;

// #926 — Auto Scroll (#691) and the all-days toggle (#892) used to be single
// booleans shared across the whole session. Now that a pill row lets the
// announcer free-switch rings (no PIN), each ring needs to remember its OWN
// preferences — Ring A can be today+auto-scroll while Ring B is all-days+
// no-scroll. Both settings live in one map, keyed by ring id, under a single
// storage key (JSON, not the old two flat booleans — those legacy keys are
// simply abandoned; nothing reads them anymore).
const RING_SETTINGS_STORAGE_KEY = "announcer-fe.ringSettings";

// Defaults for a ring that has never been switched to before — matches the
// pre-#926 defaults (auto-scroll ON, today-only) so a first-time ring doesn't
// surprise anyone.
const DEFAULT_RING_SETTINGS = { autoScrollEnabled: true, showAllDays: false };

function loadRingSettingsMap() {
  try {
    const raw = localStorage.getItem(RING_SETTINGS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function persistRingSettingsMap(map) {
  try {
    localStorage.setItem(RING_SETTINGS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage may be unavailable (private browsing, etc.) — non-fatal.
  }
}

function settingsForRing(map, ring) {
  const stored = ring ? map[ring] : null;
  return { ...DEFAULT_RING_SETTINGS, ...(stored || {}) };
}

// "2h 5m" / "5m" — coarse on purpose; this is "how long until the next
// event", not a race-clock stopwatch.
function formatCountdown(ms) {
  if (ms <= 0) return "0m";
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

// The next event's own label — a break announces its label (or the generic
// "Break"), a ride announces its class number + rider, matching the fields
// already shown on its row.
function nextEventLabel(row, t) {
  if (!row) return "";
  if (row.kind === ROW_KIND.BREAK) return row.label || t("oog.breakLabel");
  return [row.classNumber, row.riderName].filter(Boolean).join(" — ");
}

// The entity-list revalidate beat (#692 — 5s, same cadence as the status poll).
// `online` is frozen true (#912), so this always ticks now; it still pauses
// while the tab is hidden — an announcer who has switched away is not reading
// the screen. Downstream fetches are caught/cached, so a real offline stretch
// degrades gracefully rather than needing this tick paused.
function useEntityTick(online) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!online) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      setTick((n) => n + 1);
    }, ENTITY_POLL_MS);
    return () => clearInterval(id);
  }, [online]);
  return tick;
}

// #645 — a plain wall clock, always ticking (unlike useEntityTick, which
// pauses offline — staleness must be detectable EXACTLY when we're offline).
// Purely a re-render trigger for the freshness banner's math; fetches nothing.
function useNow(intervalMs) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function handleSignOut() {
  logoutCognito({
    clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
    cognitoDomain: import.meta.env.VITE_COGNITO_AUTH_DOMAIN,
    logoutUri: import.meta.env.VITE_COGNITO_POST_LOGOUT_REDIRECT_URI,
  });
}

function ClassHeader({ row }) {
  const { t } = useTranslation("announcer");
  const heading = [row.classNumber, row.className].filter(Boolean).join(" — ");
  return (
    <Box data-testid="class-header" data-class-id={row.classId} sx={{ pt: 3, pb: 0.5 }}>
      <Typography
        variant="overline"
        sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 1 }}
      >
        {t("oog.classLabel")}
      </Typography>
      <Typography variant="h4" fontWeight={800} sx={{ color: "text.primary" }}>
        {heading || t("oog.classLabel")}
      </Typography>
      <Divider sx={{ mt: 1, borderBottomWidth: 2 }} />
    </Box>
  );
}

// #892 — the all-rides/today-only toggle's day grouping. One level ABOVE a
// ClassHeader (a day can hold several classes), so it's bigger and carries
// its own foxOrange rule rather than reusing ClassHeader's divider — the
// announcer needs to tell "new class, same day" apart from "new day" at a
// glance. In the today-only view groupRowsByDay() still emits exactly one of
// these, at the very top: that's the "show the single day prominently"
// half of the AC, using the SAME component rather than a second bespoke one.
function DayHeader({ row }) {
  return (
    <Box data-testid="day-header" data-day-key={row.dayKey || ""} sx={{ pt: 2, pb: 1 }}>
      <Typography
        variant="h3"
        fontWeight={900}
        sx={{ color: "text.primary", letterSpacing: 1 }}
      >
        {dayKeyLabel(row.dayKey)}
      </Typography>
      <Divider sx={{ mt: 1, borderBottomWidth: 4, borderColor: colors.foxOrange }} />
    </Box>
  );
}

// A break is not a ride and never can be (#585/#638) — it gets its own row, and
// it is one of the things the announcer actually announces.
function BreakRow({ row }) {
  const { t } = useTranslation("announcer");
  return (
    <Box
      data-testid="break-row"
      data-break-id={row.id}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2,
        borderRadius: 3,
        border: "2px dashed",
        borderColor: colors.foxOrange,
        // Deliberately no fill: a tint that reads in light mode goes muddy in
        // dark, and the dashed orange border carries the signal in both.
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ minWidth: 96 }}>
        {formatTime(row.scheduledTime)}
      </Typography>
      <RestaurantIcon sx={{ color: colors.foxOrange }} />
      <Typography variant="h5" fontWeight={800} sx={{ color: "text.primary" }}>
        {row.label || t("oog.breakLabel")}
      </Typography>
    </Box>
  );
}

export default function OrderOfGo() {
  const navigate = useNavigate();
  const { t } = useTranslation("announcer");
  const { selectedShow, selectedRing, switchRing, unlockPicker, online, browserOnline, prefetchStatus } =
    useAnnouncerContext();
  const showId = selectedShow?.id;

  // #926 — per-ring settings map (auto-scroll, all-days), keyed by ring id.
  // `currentRingSettings` is whichever ring is active RIGHT NOW; switching
  // rings via the pill row recomputes it automatically (no extra effect
  // needed) since it's derived straight from `selectedRing`.
  const [ringSettingsMap, setRingSettingsMap] = useState(loadRingSettingsMap);
  const currentRingSettings = useMemo(
    () => settingsForRing(ringSettingsMap, selectedRing),
    [ringSettingsMap, selectedRing],
  );
  const updateRingSetting = useCallback(
    (key, value) => {
      setRingSettingsMap((prev) => {
        const next = {
          ...prev,
          [selectedRing]: { ...settingsForRing(prev, selectedRing), [key]: value },
        };
        persistRingSettingsMap(next);
        return next;
      });
    },
    [selectedRing],
  );

  // Static literal keys only — the message is chosen by branch, never by
  // t(`setup.${status}`).
  let prefetchMessage = "";
  if (prefetchStatus?.status === "loading") prefetchMessage = t("setup.prefetching");
  else if (prefetchStatus?.status === "error") prefetchMessage = t("setup.prefetchError");
  else if (prefetchStatus?.status === "done")
    prefetchMessage = t("setup.prefetchDone", {
      rides: prefetchStatus.counts?.rides ?? 0,
      breaks: prefetchStatus.counts?.breaks ?? 0,
    });

  // Entity lists move only on a scratch or a re-ingest, so a slow beat is plenty
  // (statuses have their own, much faster, poll below). Bumping the tick is what
  // makes useCachedResource revalidate — it re-runs on its deps.
  const entityTick = useEntityTick(online);

  // #892 — all-rides vs today-only toggle. Per-ring (#926): default OFF
  // (today-only) for a never-seen ring — the announcer normally works one day
  // at a time, matching scribe-fe's #910 "today by default" intent.
  const showAllDays = currentRingSettings.showAllDays;
  const setShowAllDays = useCallback(
    (enabled) => updateRingSetting("showAllDays", enabled),
    [updateRingSetting],
  );
  // "Today" as the VENUE sees it (#688), never the browser's own zone. Cheap
  // to recompute — this component already re-renders on every poll/clock tick.
  const todayKey = venueTodayKey();

  // #692 — classes now revalidate on the same entityTick as rides/breaks
  // (previously it never accepted a nonce at all, so a renamed/renumbered or
  // newly-added class could sit stale in the header forever after setup).
  const {
    data: classes,
    loading: classesLoading,
    refetch: refetchClasses,
    refreshing: classesRefreshing,
  } = useClassesForShow(showId, online, entityTick);

  // #926 — every ring for THIS show, from the already-fetched class list
  // (same source InitialSetup's picker uses — score-api has no ring-listing
  // route). Feeds the pill row below; switching a pill just calls
  // AnnouncerContext's ungated `switchRing`, no PIN involved.
  const rings = useMemo(() => ringsFor(classes), [classes]);
  const handleRingSwitch = useCallback(
    (ring) => {
      if (ring && ring !== selectedRing) switchRing(ring);
    },
    [selectedRing, switchRing],
  );

  const {
    data: rides,
    loading: ridesLoading,
    refetch: refetchRides,
    refreshing: ridesRefreshing,
  } = useRidesForRing(showId, selectedRing, online, entityTick);
  const {
    data: breaks,
    refetch: refetchBreaks,
    refreshing: breaksRefreshing,
  } = useBreaksForRing(showId, selectedRing, online, entityTick);
  const { data: people } = usePeople(online);
  const { data: horses } = useHorses(online);

  // #892 — today-only view filters the RAW entities BEFORE buildOrderOfGoRows
  // ever sees them (rather than filtering the built rows afterwards), so
  // CURRENT/ON_DECK/NEXT role assignment — and the test-fetch/status-poll ID
  // lists right below — stay scoped to today's rides, exactly like the
  // announcer's existing single-day behavior always has. A ride/break with no
  // derivable day (no scheduledTime at all) is kept regardless of the toggle:
  // it can't be dated, so it shouldn't silently vanish because of that.
  const filteredRides = useMemo(() => {
    if (showAllDays) return rides || [];
    return (rides || []).filter((r) => {
      const day = venueDayKey(r.scheduledTime);
      return !day || day === todayKey;
    });
  }, [rides, showAllDays, todayKey]);
  const filteredBreaks = useMemo(() => {
    if (showAllDays) return breaks || [];
    return (breaks || []).filter((b) => {
      const day = venueDayKey(b.scheduledTime);
      return !day || day === todayKey;
    });
  }, [breaks, showAllDays, todayKey]);

  const testIds = useMemo(
    () => (filteredRides || []).map((r) => r.testId).filter(Boolean),
    [filteredRides],
  );
  const { data: tests } = useTestsForRides(testIds, online);

  // Poll the SINGLE server-derived status for every ride in the ring (#544/#546).
  // Only meaningful while online — a hidden tab is already handled inside the hook.
  const rideIds = useMemo(() => (filteredRides || []).map((r) => r.id), [filteredRides]);
  const {
    statuses,
    lastUpdated: statusesLastUpdated,
    loading: statusesRefreshing,
    refresh: refreshStatuses,
  } = useRideStatuses(rideIds, {
    intervalMs: STATUS_POLL_MS,
    enabled: online && rideIds.length > 0,
  });

  // #908 — manual "refresh now" for a ring announcer who wants the latest
  // running order without waiting for the next poll, and without the
  // reload-the-whole-kiosk workaround (this is a PIN-locked kiosk, #734 — no
  // reload affordance exists). Fires all four data sources that make up the
  // Order of Go: classes/rides/breaks each go through useCachedResource
  // (#888's refetch() forces a fresh readLocal -> fetchRemote pass straight
  // past whatever IndexedDB has cached), and refreshStatuses() forces the
  // status poll's fetch immediately instead of waiting for STATUS_POLL_MS.
  // Deliberately does NOT touch scroll position — scrollToCurrent (#909) is
  // the only path that scrolls, and it stays gated on autoScrollEnabled
  // regardless of what triggered the data change underneath it.
  const handleRefresh = useCallback(() => {
    refetchClasses();
    refetchRides();
    refetchBreaks();
    refreshStatuses();
  }, [refetchClasses, refetchRides, refetchBreaks, refreshStatuses]);
  const refreshing =
    classesRefreshing || ridesRefreshing || breaksRefreshing || statusesRefreshing;

  // #645 — freshness banner. The status poll's own lastUpdated is the primary
  // source (it's the fastest-changing data, per docs/score/announcer.md's
  // freshness table); the setup-time prefetch timestamp is the fallback for a
  // reload that lands already offline, before any poll has ever landed.
  const lastPrefetchedAt = useLastPrefetchedAt(prefetchStatus?.status);
  const lastUpdated =
    statusesLastUpdated ?? (lastPrefetchedAt ? new Date(lastPrefetchedAt) : null);
  const now = useNow(CLOCK_TICK_MS);
  const isStale = Boolean(lastUpdated) && now - lastUpdated.getTime() > STALE_THRESHOLD_MS;
  // #691 — sticky header's live clock + countdown. Separate, faster tick from
  // the freshness banner's `now` above; pure re-render trigger, no network.
  const liveNow = useNow(LIVE_TICK_MS);
  // "Not live" whenever we're offline OR the data we're showing has gone
  // stale despite being nominally online (a dead upstream can still report
  // navigator.onLine === true) — the screen must never claim to be live when
  // it isn't sure.
  // #912 — `online` no longer tracks navigator.onLine (it's forced true so
  // reads never get gated off by a flaky-wifi false-negative). Drive the "not
  // live" banner off the RAW browser flag + real data staleness instead, so it
  // still tells the announcer honestly when the screen isn't live.
  const showFreshnessBanner = !browserOnline || isStale;

  const rows = useMemo(
    () =>
      buildOrderOfGoRows({
        rides: filteredRides,
        breaks: filteredBreaks,
        classes: classes || [],
        people: people || [],
        horses: horses || [],
        tests: tests || [],
        statuses: statuses || {},
      }),
    [filteredRides, filteredBreaks, classes, people, horses, tests, statuses],
  );

  // #892 — the all-rides/today-only toggle's own display layer, on top of
  // `rows`: inserts venue-local day headings without touching role
  // assignment (CURRENT/ON_DECK/NEXT already reflect the filtered entities
  // above) or which row anything else below refers to — findNextEvent,
  // findRoleRideId, rideRowsByClassId etc. all still read the plain `rows`.
  const displayRows = useMemo(() => groupRowsByDay(rows, todayKey), [rows, todayKey]);

  // #691 — sticky header's countdown: next ride OR break, whichever is
  // sooner (breaks are real `score-break` rows and already inline in `rows`).
  const nextEvent = useMemo(() => findNextEvent(rows, liveNow), [rows, liveNow]);

  // #644 — results/placings. The "live" class is the CURRENT ride's class:
  // that's the one whose results can still change from one status poll to the
  // next, so it's the only one that needs a slow results poll of its own
  // (docs/score/announcer.md freshness table). Every other class fetches once.
  const currentRideRow = rows.find(
    (r) => r.kind === ROW_KIND.RIDE && r.role === RIDE_ROLE.CURRENT,
  );
  const liveClassId = currentRideRow?.classId || null;

  const rideRowsByClassId = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      if (row.kind !== ROW_KIND.RIDE || !row.classId) continue;
      if (!map.has(row.classId)) map.set(row.classId, []);
      map.get(row.classId).push(row);
    }
    return map;
  }, [rows]);

  // classId -> raw GET /class/:classId/results array, lifted up from each
  // ClassResults instance so RideRow can badge its own placing too (the
  // class-level list and the per-ride badge are fed by the SAME fetch/poll,
  // never a second independent one).
  const [resultsByClassId, setResultsByClassId] = useState({});
  const handleClassResults = useCallback((classId, classResults) => {
    setResultsByClassId((prev) => {
      if (prev[classId] === classResults) return prev;
      return { ...prev, [classId]: classResults };
    });
  }, []);

  // ---- auto-scroll to the current ride, without ever fighting the announcer --
  const currentRideId = findRoleRideId(rows, RIDE_ROLE.CURRENT);
  const currentRef = useRef(null);
  const [currentOffScreen, setCurrentOffScreen] = useState(false);
  // #691 — the "Auto Scroll to Current Ride" switch. OFF leaves scrolling
  // entirely to the announcer (the effect below simply doesn't fire); ON
  // restores today's existing behavior. Per-ring (#926) — same map as
  // showAllDays above, so it survives a reload AND a ring switch restores
  // THIS ring's own preference rather than whatever the last ring had.
  const autoScrollEnabled = currentRingSettings.autoScrollEnabled;
  const setAutoScrollEnabled = useCallback(
    (enabled) => updateRingSetting("autoScrollEnabled", enabled),
    [updateRingSetting],
  );

  // #909 — the ONE place that ever calls scrollIntoView for "jump to
  // current". Gated here so every caller (the effect below AND the "back to
  // current" FAB, which docs/score/announcer.md describes as part of the
  // same auto-scroll feature — "get back with one tap") is a no-op while the
  // switch is OFF. OFF must mean zero programmatic scrolling, full stop.
  const scrollToCurrent = useCallback(() => {
    if (!autoScrollEnabled) return;
    currentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [autoScrollEnabled]);

  // #919 — a LIVE-SHOW regression. Confirming a ride's marks (all judges
  // confirmed, #697/#698) flips that ride's OWN status off IN_PROGRESS, so
  // assignRoles() (orderOfGo.js) hands CURRENT to whichever OTHER ride is
  // still IN_PROGRESS. Auto-scrolling to EVERY CURRENT change yanked the
  // screen away from the ride the announcer was mid-read on, right as its
  // judge marks were revealed — exactly when the operator needs to control
  // position, not have it taken from them.
  //
  // Distinguish WHY current changed by looking at the OUTGOING current
  // ride's own status this render, not the incoming one:
  //   - it left IN_PROGRESS (confirmed / completed / final / scratched) ->
  //     CURRENT moved because the ride the operator was looking at is DONE,
  //     not because the ring genuinely moved on. Don't scroll — #919.
  //   - it's still IN_PROGRESS -> a later ride has genuinely overtaken it
  //     (#921's multi-IN_PROGRESS case, e.g. the next horse already entered
  //     before the previous one's marks were confirmed) -> that's real ring
  //     progression, follow it.
  //   - no previous current at all -> first paint, scroll straight to it.
  // The "back to current" FAB is always there for the operator to jump
  // manually regardless of which case suppressed the auto-scroll.
  //
  // statusByRideId is kept in a REF, not a scroll-effect dependency: `rows`
  // (and therefore a status map derived from it) gets a new reference on
  // EVERY poll tick, including ones where currentRideId itself doesn't
  // change. Depending on it directly would re-run the decision below — and
  // therefore potentially re-scroll — on every tick, not only on an actual
  // CURRENT change. The effect must stay keyed on currentRideId alone,
  // exactly like the pre-#919 version, and just read the freshest status
  // snapshot when it does fire.
  const statusByRideIdRef = useRef(new Map());
  useEffect(() => {
    const map = new Map();
    for (const row of rows) {
      if (row.kind === ROW_KIND.RIDE) map.set(row.id, row.status);
    }
    statusByRideIdRef.current = map;
  }, [rows]);

  const prevCurrentRideIdRef = useRef(null);
  useEffect(() => {
    if (!currentRideId) return;
    const prevCurrentRideId = prevCurrentRideIdRef.current;
    const isReassignment = prevCurrentRideId && prevCurrentRideId !== currentRideId;
    const outgoingStillInProgress =
      isReassignment &&
      statusByRideIdRef.current.get(prevCurrentRideId) === RIDE_STATUSES.IN_PROGRESS;
    if (!isReassignment || outgoingStillInProgress) {
      scrollToCurrent();
    }
    prevCurrentRideIdRef.current = currentRideId;
  }, [currentRideId, scrollToCurrent]);

  useEffect(() => {
    if (!currentRideId) {
      setCurrentOffScreen(false);
      return undefined;
    }
    const check = () => {
      const el = currentRef.current;
      if (!el) return;
      const { top, bottom } = el.getBoundingClientRect();
      setCurrentOffScreen(bottom < 0 || top > window.innerHeight);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [currentRideId, rows.length]);

  const handleUnlock = () => {
    unlockPicker();
    navigate("/setup");
  };

  const loading = (classesLoading || ridesLoading) && rows.length === 0;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 12 }}>
      <PinLock onUnlock={handleUnlock} testId="announcer-lock" />

      {/* #691 — sticky header: everything that was previously "above the
          Classes list" (show/ring/sign-out, freshness banner, prefetch
          status), plus the live venue clock, the next-event countdown, and
          the Auto Scroll switch. Stays pinned while `oog-list` below scrolls
          under it — no AppBar in this app, so `top: 0` is correct. */}
      <Box
        data-testid="oog-sticky-header"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          px: 3,
          pt: 3,
          pb: 2,
        }}
      >
        <Box sx={{ maxWidth: 1100, mx: "auto" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <Box>
              <Typography variant="h4" fontWeight={800} data-testid="oog-show-name">
                {selectedShow?.name}
              </Typography>
              {/* #926 — free ring switcher: a pill per ring on THIS show,
                  clicking one calls the ungated AnnouncerContext.switchRing
                  immediately (no PIN). Org/show change is untouched — still
                  only reachable through the PIN lock below. Active pill uses
                  brand foxOrange + black text (same treatment the old
                  read-only ring Chip used); inactive pills are outlined with
                  `text.primary`/`divider` — legible in both themes, avoiding
                  brand `primary.main`'s near-black grey on dark (#862). */}
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                data-testid="oog-ring"
                sx={{ mt: 1 }}
              >
                {rings.map((ring) => {
                  const active = ring === selectedRing;
                  return (
                    <Chip
                      key={ring}
                      data-testid={`ring-pill-${ring}`}
                      label={`${t("setup.ringLabel")} ${ring}`}
                      onClick={() => handleRingSwitch(ring)}
                      variant={active ? "filled" : "outlined"}
                      aria-pressed={active}
                      sx={
                        active
                          ? {
                              fontSize: "1rem",
                              fontWeight: 800,
                              bgcolor: colors.foxOrange,
                              color: "#000",
                              "&:hover": { bgcolor: colors.foxOrange },
                            }
                          : {
                              fontSize: "1rem",
                              fontWeight: 700,
                              color: "text.primary",
                              borderColor: "divider",
                            }
                      }
                    />
                  );
                })}
              </Stack>
            </Box>

            <Stack direction="row" spacing={3} alignItems="flex-start">
              <Box sx={{ textAlign: "right" }}>
                <Typography
                  variant="h3"
                  fontWeight={800}
                  data-testid="oog-clock"
                  sx={{ color: "text.primary", fontVariantNumeric: "tabular-nums" }}
                >
                  {formatVenueClock(liveNow)}
                </Typography>
                <Typography
                  variant="body2"
                  data-testid="oog-countdown"
                  sx={{ color: "text.secondary" }}
                >
                  {nextEvent
                    ? t("oog.nextEvent", {
                        label: nextEventLabel(nextEvent, t),
                        countdown: formatCountdown(
                          new Date(nextEvent.scheduledTime).getTime() - liveNow,
                        ),
                      })
                    : t("oog.nextEventNone")}
                </Typography>
              </Box>
              <Button
                variant="text"
                color="inherit"
                startIcon={<LogoutIcon />}
                onClick={handleSignOut}
                sx={{ color: "text.secondary" }}
              >
                {t("setup.signOut")}
              </Button>

              {/* #908 — manual refresh, in the sticky header's own top row
                  (not a viewport-fixed overlay — that positioned relative to
                  the CSS layout viewport and, on a narrower/actual window,
                  landed on top of the Sign Out button and clipped it; a
                  normal flex item here can never overlap by construction).
                  Own bg/border/shadow, NOT color="primary" — brand
                  primary.main is a near-black grey that all but disappears
                  in dark mode (#862) — matching scribe-fe's #888 refresh
                  button. */}
              <Tooltip title={refreshing ? t("oog.refreshing") : t("oog.refresh")}>
                <span>
                  <IconButton
                    data-testid="oog-refresh-button"
                    aria-label={t("oog.refresh")}
                    onClick={handleRefresh}
                    disabled={refreshing}
                    sx={{
                      bgcolor: "background.paper",
                      color: "text.primary",
                      border: 1,
                      borderColor: "divider",
                      boxShadow: 2,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <RefreshIcon
                      fontSize="small"
                      sx={
                        refreshing
                          ? {
                              animation: "oogRefreshSpin 1s linear infinite",
                              "@keyframes oogRefreshSpin": {
                                from: { transform: "rotate(0deg)" },
                                to: { transform: "rotate(360deg)" },
                              },
                            }
                          : undefined
                      }
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={3} flexWrap="wrap">
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={autoScrollEnabled}
                  onChange={(e) => setAutoScrollEnabled(e.target.checked)}
                  data-testid="auto-scroll-toggle"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: colors.foxOrange },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: colors.foxOrange,
                    },
                  }}
                />
              }
              label={t("oog.autoScrollLabel")}
              slotProps={{ typography: { sx: { color: "text.primary" } } }}
            />

            {/* #892 — all-rides vs today-only. Coexists with Auto Scroll in
                the same row rather than competing for the same switch or
                fighting #909's scroll gating: this toggle only ever changes
                WHICH rides feed buildOrderOfGoRows, never scroll behavior. */}
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={showAllDays}
                  onChange={(e) => setShowAllDays(e.target.checked)}
                  data-testid="show-all-days-toggle"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: colors.foxOrange },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: colors.foxOrange,
                    },
                  }}
                />
              }
              label={t("oog.showAllDaysLabel")}
              slotProps={{ typography: { sx: { color: "text.primary" } } }}
            />
          </Stack>

          {/* #645 — persistent, honest freshness banner. Shows whenever the app
              is offline OR the cached data has gone stale despite being
              nominally online; clears itself the moment a refresh lands. */}
          {showFreshnessBanner ? (
            <Alert severity="warning" sx={{ mt: 2 }} data-testid="oog-not-live">
              {lastUpdated
                ? t("oog.notLive", { time: formatClockTime(lastUpdated) })
                : t("oog.notLiveNoData")}
            </Alert>
          ) : null}

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1 }}
            data-testid="prefetch-status"
          >
            {prefetchMessage}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 3, maxWidth: 1100, mx: "auto" }}>
        {/* #714 — the running-order list not yet loaded: shaped skeleton
            rows instead of a bare spinner, matching the table-data
            convention used everywhere else. */}
        {loading ? (
          <Box sx={{ mt: 3 }} data-testid="oog-loading">
            <TableSkeleton rows={8} columns={4} />
          </Box>
        ) : null}

        {!loading && rows.length === 0 ? (
          <Typography sx={{ mt: 8 }} color="text.secondary" data-testid="oog-empty">
            {t("oog.empty")}
          </Typography>
        ) : null}

        <Stack spacing={1.5} sx={{ mt: 2 }} data-testid="oog-list">
          {displayRows.map((row) => {
            if (row.kind === ROW_KIND.DAY_HEADER) {
              return <DayHeader key={row.id} row={row} />;
            }
            if (row.kind === ROW_KIND.CLASS_HEADER) {
              return (
                <Box key={row.id}>
                  <ClassHeader row={row} />
                  <ClassResults
                    classId={row.classId}
                    isLive={row.classId === liveClassId}
                    rideRows={rideRowsByClassId.get(row.classId) || []}
                    onResults={handleClassResults}
                    online={browserOnline}
                  />
                </Box>
              );
            }
            if (row.kind === ROW_KIND.BREAK) {
              return <BreakRow key={row.id} row={row} />;
            }
            const classResults = resultsByClassId[row.classId];
            const rideResult = classResults?.find((r) => r.rideId === row.id) || null;
            return (
              <RideRow
                key={row.id}
                row={row}
                result={rideResult}
                ref={row.role === RIDE_ROLE.CURRENT ? currentRef : null}
              />
            );
          })}
        </Stack>
      </Box>

      {currentOffScreen && autoScrollEnabled ? (
        <Fab
          variant="extended"
          data-testid="back-to-current"
          onClick={scrollToCurrent}
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: colors.foxOrange,
            color: "#000",
            fontWeight: 800,
            "&:hover": { bgcolor: colors.foxOrange },
          }}
        >
          <ArrowDownwardIcon sx={{ mr: 1 }} />
          {t("oog.backToCurrent")}
        </Fab>
      ) : null}
    </Box>
  );
}
